import {safeStorage} from "../safe-storage.js"

const store = safeStorage("local")

export default {
  mounted() {
    const el = this.el
    const DISMISS_KEY = "feedback_fab_dismissed"
    this.feedbackDestroyed = false
    this.feedbackTimeouts = new Set()
    let startX = 0
    let startY = 0
    let currentX = 0
    let dragging = false

    // Hide on feedback page itself
    if (window.location.pathname === "/feedback") {
      el.style.display = "none"
      return
    }

    // Check if previously dismissed
    if (store.getItem(DISMISS_KEY) === "true") {
      this.dismiss(false)
    }

    // Set up modal form handling
    this.setupModal()

    // Touch start
    el.addEventListener("touchstart", (e) => {
      const touch = e.touches[0]
      startX = touch.clientX
      startY = touch.clientY
      currentX = 0
      dragging = false
      el.style.transition = "none"
    }, { passive: true })

    // Touch move — only track horizontal swipes
    el.addEventListener("touchmove", (e) => {
      const touch = e.touches[0]
      const dx = touch.clientX - startX
      const dy = touch.clientY - startY

      if (!dragging && Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) {
        dragging = true
      }

      if (dragging && dx > 0) {
        currentX = dx
        el.style.transform = `translateX(${dx}px)`
        el.style.opacity = Math.max(0, 1 - dx / 150)
        e.preventDefault()
      }
    }, { passive: false })

    // Touch end — dismiss if swiped far enough, else snap back
    el.addEventListener("touchend", () => {
      el.style.transition = "transform 0.3s ease, opacity 0.3s ease"
      if (dragging && currentX > 80) {
        this.dismiss(true)
      } else {
        el.style.transform = ""
        el.style.opacity = ""
      }
      dragging = false
    }, { passive: true })
  },

  setupModal() {
    const modal = document.getElementById("feedback-modal")
    const form = document.getElementById("feedback-modal-form")
    const status = document.getElementById("feedback-modal-status")
    const submitBtn = document.getElementById("feedback-submit-btn")
    const pageUrlInput = document.getElementById("feedback-page-url")
    const fileInput = document.getElementById("feedback-files")
    const previews = document.getElementById("feedback-previews")
    if (!modal || !form) return

    const MAX_FILES = 3
    const MAX_SIZE = 5 * 1024 * 1024 // 5MB
    let pendingFiles = []

    this.feedbackObserver = new MutationObserver(() => {
      if (modal.open && pageUrlInput) {
        pageUrlInput.value = window.location.pathname
      }
    })
    this.feedbackObserver.observe(modal, { attributes: true, attributeFilter: ["open"] })

    const addImageFile = (file) => {
      if (pendingFiles.length >= MAX_FILES) return
      if (file.size > MAX_SIZE) {
        status.textContent = `${file.name} is too large (max 5MB).`
        status.className = "text-sm rounded-lg p-3 bg-error/10 text-error"
        return
      }
      if (!file.type.startsWith("image/")) return

      const reader = new FileReader()
      reader.onload = () => {
        const base64 = reader.result.split(",")[1]
        pendingFiles.push({ name: file.name, data: base64 })
        renderPreviews()
      }
      reader.readAsDataURL(file)
    }

    if (fileInput && previews) {
      fileInput.addEventListener("change", () => {
        Array.from(fileInput.files).forEach(addImageFile)
        fileInput.value = ""
      })
    }

    this.feedbackPasteHandler = (e) => {
      if (!modal.open) return
      const items = Array.from(e.clipboardData?.items || [])
      const imageItems = items.filter(item => item.type.startsWith("image/"))
      if (imageItems.length === 0) return

      e.preventDefault()
      for (const item of imageItems) {
        const file = item.getAsFile()
        if (!file) continue
        const ext = file.type.split("/")[1] || "png"
        const name = `screenshot-${Date.now()}.${ext}`
        addImageFile(new File([file], name, { type: file.type }))
      }
    }
    document.addEventListener("paste", this.feedbackPasteHandler)

    const renderPreviews = () => {
      if (!previews) return
      previews.innerHTML = ""
      pendingFiles.forEach((f, i) => {
        const wrap = document.createElement("div")
        wrap.className = "relative group"
        const img = document.createElement("img")
        img.src = `data:image/png;base64,${f.data}`
        img.className = "w-16 h-16 object-cover rounded-lg border border-base-300"
        img.alt = f.name
        const btn = document.createElement("button")
        btn.type = "button"
        btn.className = "absolute -top-1.5 -right-1.5 w-5 h-5 bg-error text-error-content rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
        btn.innerHTML = "&times;"
        btn.onclick = () => { pendingFiles.splice(i, 1); renderPreviews() }
        wrap.appendChild(img)
        wrap.appendChild(btn)
        previews.appendChild(wrap)
      })
      const label = document.getElementById("feedback-file-label")
      if (label) {
        const span = label.querySelector("span")
        if (span) {
          span.textContent = pendingFiles.length > 0
            ? `${pendingFiles.length}/3 screenshot${pendingFiles.length > 1 ? "s" : ""}`
            : "Add screenshots or paste from clipboard"
        }
      }
    }

    form.querySelectorAll('input[name="category"]').forEach(radio => {
      radio.addEventListener("change", () => {
        form.querySelectorAll(".feedback-cat-option").forEach(opt => {
          opt.classList.remove("ring-2", "ring-primary")
          opt.classList.add("ring-0")
        })
        radio.closest(".feedback-cat-option").classList.remove("ring-0")
        radio.closest(".feedback-cat-option").classList.add("ring-2", "ring-primary")
      })
    })

    form.addEventListener("submit", async (e) => {
      e.preventDefault()
      const formData = Object.fromEntries(new FormData(form))
      const csrfToken = document.querySelector("meta[name='csrf-token']").getAttribute("content")

      const payload = {
        ...formData,
        attachments: pendingFiles.map(f => ({ name: f.name, data: f.data })),
      }

      submitBtn.disabled = true
      submitBtn.textContent = pendingFiles.length > 0 ? "Uploading..." : "Submitting..."
      status.classList.add("hidden")

      try {
        const res = await fetch("/api/feedback", {
          method: "POST",
          headers: {
            "content-type": "application/json",
            "x-csrf-token": csrfToken,
          },
          body: JSON.stringify(payload),
        })

        const body = await res.json()

        if (res.ok) {
          status.textContent = "Thanks! Your feedback has been submitted."
          status.className = "text-sm rounded-lg p-3 bg-success/10 text-success"
          form.reset()
          pendingFiles = []
          renderPreviews()
          const bugRadio = form.querySelector('input[value="bug"]')
          if (bugRadio) bugRadio.checked = true
          form.querySelectorAll(".feedback-cat-option").forEach(opt => {
            opt.classList.remove("ring-2", "ring-primary")
            opt.classList.add("ring-0")
          })
          const firstOpt = form.querySelector('.feedback-cat-option[data-value="bug"]')
          if (firstOpt) {
            firstOpt.classList.remove("ring-0")
            firstOpt.classList.add("ring-2", "ring-primary")
          }
          if (!this.feedbackDestroyed) {
            const closeTimeout = setTimeout(() => {
              this.feedbackTimeouts.delete(closeTimeout)
              modal.close()
            }, 1500)
            this.feedbackTimeouts.add(closeTimeout)
          }
        } else {
          status.textContent = body.error || "Something went wrong."
          status.className = "text-sm rounded-lg p-3 bg-error/10 text-error"
        }
      } catch (_) {
        status.textContent = "Network error. Please try again."
        status.className = "text-sm rounded-lg p-3 bg-error/10 text-error"
      } finally {
        submitBtn.disabled = false
        submitBtn.textContent = "Submit"
      }
    })

    modal.addEventListener("close", () => {
      status.classList.add("hidden")
    })
  },

  destroyed() {
    this.feedbackDestroyed = true
    this.feedbackObserver?.disconnect()

    if (this.feedbackPasteHandler) {
      document.removeEventListener("paste", this.feedbackPasteHandler)
    }

    this.feedbackTimeouts?.forEach(timeout => clearTimeout(timeout))
    this.feedbackTimeouts?.clear()
    this.feedbackTab?.remove()
    this.feedbackTab = null
  },

  dismiss(animate) {
    const el = this.el
    if (animate) {
      el.style.transform = "translateX(calc(100% + 20px))"
      el.style.opacity = "0"
    } else {
      el.style.transition = "none"
      el.style.transform = "translateX(calc(100% + 20px))"
      el.style.opacity = "0"
    }
    store.setItem("feedback_fab_dismissed", "true")

    const dismissTimeout = setTimeout(() => {
      this.feedbackTimeouts.delete(dismissTimeout)
      if (this.feedbackDestroyed) return
      if (document.getElementById("feedback-fab-tab")) return
      // Phones hide the FAB (feedback lives in the More sheet): no restore tab.
      if (window.matchMedia && !window.matchMedia("(min-width: 768px)").matches) return
      const tab = document.createElement("div")
      this.feedbackTab = tab
      tab.id = "feedback-fab-tab"
      tab.style.cssText = `
        position: fixed; bottom: 24px; right: 0; z-index: 50;
        width: 24px; height: 48px;
        background: var(--color-primary); border-radius: 8px 0 0 8px;
        cursor: pointer; opacity: 0.5;
        transition: opacity 0.2s, width 0.2s;
        display: flex; align-items: center; justify-content: center;
      `
      tab.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" style="width:14px;height:14px;color:var(--color-primary-content);"><path fill-rule="evenodd" d="M11.78 5.22a.75.75 0 0 1 0 1.06L8.06 10l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z" clip-rule="evenodd" /></svg>'

      tab.addEventListener("mouseenter", () => { tab.style.opacity = "0.9"; tab.style.width = "32px" })
      tab.addEventListener("mouseleave", () => { tab.style.opacity = "0.5"; tab.style.width = "24px" })

      tab.addEventListener("click", () => {
        tab.remove()
        this.feedbackTab = null
        store.removeItem("feedback_fab_dismissed")
        el.style.transition = "transform 0.3s ease, opacity 0.3s ease"
        el.style.transform = ""
        el.style.opacity = ""
      })

      tab.addEventListener("touchstart", () => {
        tab.remove()
        this.feedbackTab = null
        store.removeItem("feedback_fab_dismissed")
        el.style.transition = "transform 0.3s ease, opacity 0.3s ease"
        el.style.transform = ""
        el.style.opacity = ""
      }, { passive: true })

      document.body.appendChild(tab)
    }, animate ? 350 : 0)
    this.feedbackTimeouts.add(dismissTimeout)
  }
}
